import type { DateTimePreferences } from "@workspace/types/src/entities/user";

/**
 * Default preferences for fallback when user preferences are not available
 */
export const DEFAULT_DATETIME_PREFERENCES: DateTimePreferences = {
	time_format: "12h",
	date_format: "us",
};

/**
 * Parse database timestamp format to Date object
 * Handles "YYYY-MM-DD HH:MM:SS" format from Postgres
 * @param dateStr - Database timestamp string
 * @returns Date object
 */
function parseDatabaseTimestamp(dateStr: string): Date {
	// Check if this is a database timestamp (space-separated, no timezone)
	const isDatabaseFormat = 
		dateStr.includes(" ") && 
		!dateStr.includes("Z") && 
		!dateStr.match(/[+-]\d{2}:\d{2}$/);
	
	if (isDatabaseFormat) {
		// Parse as naive datetime - venue times should display exactly as stored
		// Don't add timezone info - treat as local time for display purposes
		return new Date(`${dateStr.replace(" ", "T")}`);
	}
	
	// Already in ISO format or has timezone
	return new Date(dateStr);
}

/**
 * Format time based on user preferences
 * @param date - The date object to format
 * @param preferences - User's datetime preferences (optional, defaults to 12h format)
 * @returns Formatted time string
 */
export function formatTimeWithPreferences(
	date: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { time_format = "12h" } = preferences;

	// Check if this is a database timestamp to determine UTC handling
	const isDatabaseTimestamp =
		typeof date === "string" &&
		date.includes(" ") &&
		!date.includes("Z") &&
		!date.match(/[+-]\d{2}:\d{2}$/);

	// Parse the date appropriately
	const dateObj = typeof date === "string" ? parseDatabaseTimestamp(date) : date;

	// Check if the date is valid
	if (!dateObj || isNaN(dateObj.getTime())) {
		console.warn("Invalid date in formatTimeWithPreferences:", date);
		return "Invalid Time";
	}

	// Extract hours and minutes using local time methods
	// Database timestamps are now parsed without timezone conversion
	const hours = dateObj.getHours();
	const minutes = dateObj.getMinutes();

	// Check if hours/minutes are valid numbers
	if (isNaN(hours) || isNaN(minutes)) {
		console.warn("Invalid hours/minutes in formatTimeWithPreferences:", { date, hours, minutes });
		return "Invalid Time";
	}

	if (time_format === "24h") {
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
	}

	// Default to 12h format
	// Fix: Properly handle noon (12 PM) and midnight (12 AM)
	const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
	// Fix: hours === 12 should be PM, not AM
	const ampm = hours >= 12 ? "PM" : "AM";
	const minuteStr = minutes.toString().padStart(2, "0");

	// Remove space before AM/PM for consistency
	return `${hour12}:${minuteStr}${ampm}`;
}

/**
 * Format date based on user preferences
 * @param date - The date object to format
 * @param preferences - User's datetime preferences (optional, defaults to US format)
 * @returns Formatted date string
 */
export function formatDateWithPreferences(
	date: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { date_format = "us" } = preferences;
	const dateObj = typeof date === "string" ? new Date(date) : date;

	if (date_format === "international") {
		return dateObj.toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	}

	// Default to US format
	return dateObj.toLocaleDateString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric",
	});
}

/**
 * Format time range based on user preferences (for set times)
 * @param startTime - Start time string or Date
 * @param endTime - End time string or Date (optional)
 * @param preferences - User's datetime preferences
 * @returns Formatted time range string
 */
export function formatTimeRangeWithPreferences(
	startTime: Date | string,
	endTime?: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const formattedStart = formatTimeWithPreferences(startTime, preferences);

	if (!endTime) {
		return `${formattedStart}-`;
	}

	const formattedEnd = formatTimeWithPreferences(endTime, preferences);
	return `${formattedStart}-${formattedEnd}`;
}

/**
 * Enhanced set time formatter with day and preferences support
 * Replaces the existing formatSetTime function with preference awareness
 */
export function formatSetTimeWithPreferences(
	startTime: Date | string,
	endTime?: Date | string,
	preferences: Partial<DateTimePreferences> = {},
	includeDay = true,
): string {
	if (!startTime) {
		return "Set Time: TBA";
	}

	const startDate =
		typeof startTime === "string" ? parseDatabaseTimestamp(startTime) : startTime;

	// Extract day name from the venue date (no timezone conversion)
	const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	const dayName = dayNames[startDate.getDay()];
	const timeRange = formatTimeRangeWithPreferences(
		startTime,
		endTime,
		preferences,
	);

	return includeDay ? `${dayName} ${timeRange}` : timeRange;
}

/**
 * Format show time range handling database timestamp formats
 * Intelligently handles both full timestamps and time-only formats
 * @param timeStart - Start time (database format "YYYY-MM-DD HH:MM:SS" or time-only)
 * @param timeEnd - End time (optional, same formats as timeStart)
 * @param preferences - User's datetime preferences
 * @returns Formatted time range string
 */
export function formatShowTimeRange(
	timeStart?: string | null,
	timeEnd?: string | null,
	preferences: Partial<DateTimePreferences> = {},
): string {
	if (!timeStart) return "Time TBA";


	try {
		// Parse time string intelligently
		const parseTimeString = (timeStr: string): Date => {
			// If it contains date information, use our helper
			if (timeStr.includes("T") || timeStr.includes(" ") || timeStr.length > 8) {
				return parseDatabaseTimestamp(timeStr);
			}
			
			// Otherwise treat as time-only format (HH:MM or HH:MM:SS)
			const [hours, minutes] = timeStr.split(":").map(Number);
			const date = new Date();
			date.setHours(hours || 0, minutes || 0, 0, 0);
			return date;
		};

		const startDate = parseTimeString(timeStart);
		const endDate = timeEnd ? parseTimeString(timeEnd) : undefined;

		return formatTimeRangeWithPreferences(startDate, endDate, preferences);
	} catch (error) {
		console.warn("Error formatting show time range:", error);
		return timeStart + (timeEnd ? ` - ${timeEnd}` : "");
	}
}

/**
 * Format festival date with preferences (for short date displays)
 * @param date - Date to format
 * @param preferences - User's datetime preferences
 * @returns Formatted date string like "Jan 1, 2025" or "1 Jan 2025"
 */
export function formatShortDateWithPreferences(
	date: Date | string | null,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { date_format = "us" } = preferences;

	// Handle null/undefined dates
	if (!date) {
		return "TBA";
	}

	// Parse dates appropriately
	const dateObj =
		typeof date === "string"
			? (() => {
					// Check if it looks like a timestamp (has time component)
					if (date.includes(" ") || date.includes("T")) {
						return parseDatabaseTimestamp(date);
					}
					// Handle date-only strings like "2025-07-31"
					const [year, month, day] = date.split("-").map(Number);
					if (!year || !month || !day) {
						return new Date(); // fallback to current date if invalid
					}
					return new Date(year, month - 1, day);
				})()
			: date;
	
	// Track if this was a database timestamp for UTC handling
	const isDatabaseTimestamp =
		typeof date === "string" &&
		date.includes(" ") &&
		!date.includes("Z") &&
		!date.match(/[+-]\d{2}:\d{2}$/);

	// Check if date is valid
	if (!dateObj || Number.isNaN(dateObj.getTime())) {
		return "TBA";
	}

	// For database timestamps, use UTC methods to get the correct date
	if (isDatabaseTimestamp) {
		const monthNames = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];
		const month = monthNames[dateObj.getUTCMonth()];
		const day = dateObj.getUTCDate();
		const year = dateObj.getUTCFullYear();

		if (date_format === "international") {
			return `${day} ${month} ${year}`;
		}
		// US format
		return `${month} ${day}, ${year}`;
	}

	// For other dates, use normal locale methods
	if (date_format === "international") {
		return dateObj.toLocaleDateString("en-GB", {
			day: "numeric",
			month: "short",
			year: "numeric",
		});
	}

	// Default to US format
	return dateObj.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

/**
 * Format festival date range with preferences
 * @param startDate - Festival start date
 * @param endDate - Festival end date (optional)
 * @param preferences - User's datetime preferences
 * @returns Formatted date range string
 */
export function formatFestivalDatesWithPreferences(
	startDate: Date | string,
	endDate?: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { date_format = "us" } = preferences;

	// Parse dates without timezone conversion for date-only strings
	const start =
		typeof startDate === "string"
			? startDate.includes("T")
				? new Date(startDate)
				: (() => {
						const [year, month, day] = startDate.split("-").map(Number);
						if (!year || !month || !day) {
							return new Date(); // fallback to current date if invalid
						}
						return new Date(year, month - 1, day);
					})()
			: startDate;

	if (!endDate || startDate === endDate) {
		return formatShortDateWithPreferences(start, preferences);
	}

	const end =
		typeof endDate === "string"
			? endDate.includes("T")
				? new Date(endDate)
				: (() => {
						const [year, month, day] = endDate.split("-").map(Number);
						if (!year || !month || !day) {
							return new Date(); // fallback to current date if invalid
						}
						return new Date(year, month - 1, day);
					})()
			: endDate;

	// Same month and year
	if (
		start.getMonth() === end.getMonth() &&
		start.getFullYear() === end.getFullYear()
	) {
		if (date_format === "international") {
			const monthYear = start.toLocaleDateString("en-GB", {
				month: "short",
				year: "numeric",
			});
			return `${start.getDate()}-${end.getDate()} ${monthYear}`;
		}

		const monthYear = start.toLocaleDateString("en-US", {
			month: "short",
			year: "numeric",
		});
		return `${monthYear.replace(/ \d{4}/, "")} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
	}

	// Different months or years
	const startFormatted = formatShortDateWithPreferences(start, preferences);
	const endFormatted = formatShortDateWithPreferences(end, preferences);
	return `${startFormatted} - ${endFormatted}`;
}

/**
 * Format time in compact format for calendar axis labels (e.g., "9P" or "21")
 * @param date - The date object to format
 * @param preferences - User's datetime preferences (optional, defaults to 12h format)
 * @returns Compact formatted time string
 */
export function formatCompactTimeWithPreferences(
	date: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { time_format = "12h" } = preferences;
	const dateObj = typeof date === "string" ? new Date(date) : date;
	const hour = dateObj.getHours();

	if (time_format === "24h") {
		return hour.toString().padStart(2, "0");
	}

	// 12h format - ultra-compact version for mobile (9P, not 9PM) to prevent clipping
	if (hour === 0) return "12A";
	if (hour === 12) return "12P";
	if (hour < 12) return `${hour}A`;
	return `${hour - 12}P`;
}

/**
 * Format join date with preferences
 * @param dateString - Join date string
 * @param preferences - User's datetime preferences
 * @returns Formatted join date like "Joined Mar 2025" or "Joined Mar 2025"
 */
export function formatJoinDateWithPreferences(
	dateString: string,
	_preferences: Partial<DateTimePreferences> = {},
): string {
	const date = new Date(dateString);

	// Both formats use the same short month format for join dates
	// This maintains a clean, consistent look across locales
	return `Joined ${date.toLocaleDateString("en-US", {
		month: "short",
		year: "numeric",
	})}`;
}

/**
 * Format date with full weekday name based on user preferences
 * @param date - The date object to format
 * @param preferences - User's datetime preferences (optional, defaults to US format)
 * @returns Formatted date string like "Saturday, December 28, 2024" or "Saturday, 28 December 2024"
 */
export function formatLongDateWithPreferences(
	date: Date | string,
	preferences: Partial<DateTimePreferences> = {},
): string {
	const { date_format = "us" } = preferences;
	const dateObj =
		typeof date === "string" ? new Date(`${date}T00:00:00`) : date;

	if (date_format === "international") {
		return dateObj.toLocaleDateString("en-GB", {
			weekday: "long",
			day: "numeric",
			month: "long",
			year: "numeric",
		});
	}

	// Default to US format
	return dateObj.toLocaleDateString("en-US", {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

/**
 * Get user's preferences with fallback to defaults
 * Utility to safely extract preferences from user profile data
 */
export function getUserPreferences(
	userProfile?: {
		time_format?: "12h" | "24h";
		date_format?: "us" | "international";
		location_preference?: string | null;
	} | null,
): DateTimePreferences {
	return {
		time_format:
			userProfile?.time_format ?? DEFAULT_DATETIME_PREFERENCES.time_format,
		date_format:
			userProfile?.date_format ?? DEFAULT_DATETIME_PREFERENCES.date_format,
		location_preference: userProfile?.location_preference ?? null,
	};
}
